import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Pencil, Plus, Star, Trash2 } from "lucide-react";
import { api, errMsg, inr } from "@/lib/api";
import { refreshTripsCache } from "@/hooks/useTrips";
import { TripFormDialog } from "./TripFormDialog";

export function TripsManager({ onChange }) {
  const [trips, setTrips] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/trips").then((r) => setTrips(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const changed = () => {
    refreshTripsCache();
    load();
    onChange?.();
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try {
      await api.delete(`/admin/trips/${t.id}`);
      toast.success("Trip deleted");
      changed();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-trips-manager">
      <div className="flex items-center justify-between mb-6">
        <p className="text-cream/70">{trips.length} trips</p>
        <button onClick={() => setEditing({})} data-testid="admin-add-trip-btn"
          className="inline-flex items-center gap-2 rounded-full bg-terra px-5 py-2.5 font-semibold hover:bg-[#a5370a] transition-colors">
          <Plus className="h-4 w-4" /> Add trip
        </button>
      </div>
      <div className="space-y-3">
        {trips.map((t) => (
          <div key={t.id} data-testid={`admin-trip-row-${t.slug}`}
            className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-3xl bg-cream/5 border border-cream/10 p-3 pr-4">
            <img src={t.image} alt="" className="h-20 w-full sm:w-28 object-cover rounded-2xl" />
            <div className="flex-1 min-w-0">
              <p className="font-display text-lg font-bold truncate flex items-center gap-2">
                {t.featured && <Star className="h-4 w-4 text-gold fill-gold shrink-0" />}{t.title}
              </p>
              <p className="text-sm text-cream/70 font-mono">
                {t.destination} · {t.nights}N/{t.days}D · {inr(t.price)} · {t.region}
                {!t.active && <span className="ml-2 text-terra-soft">HIDDEN</span>}
              </p>
            </div>
            <div className="flex gap-2">
              <Link to={`/trips/${t.slug}`} target="_blank" className="grid place-items-center h-10 w-10 rounded-full border border-cream/20 hover:bg-cream hover:text-ink transition-colors" aria-label="View"><Eye className="h-4 w-4" /></Link>
              <button onClick={() => setEditing(t)} data-testid={`admin-edit-trip-${t.slug}`} aria-label="Edit" className="grid place-items-center h-10 w-10 rounded-full border border-cream/20 hover:bg-cream hover:text-ink transition-colors"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(t)} data-testid={`admin-delete-trip-${t.slug}`} aria-label="Delete" className="grid place-items-center h-10 w-10 rounded-full border border-cream/20 hover:bg-terra hover:border-terra transition-colors"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && <TripFormDialog trip={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); changed(); }} />}
    </div>
  );
}
